<template>
  <v-dialog
      v-model="dialog"
      max-width="1000px"
      persistent
      scrollable
  >
    <v-card>
      <v-card-title class="headline">
        <v-icon left color="orange">mdi-clock-edit-outline</v-icon>
        Adjust Smart Offer Launch Times
        <v-spacer/>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text style="max-height: 70vh">
        <!-- Selected Offers Info -->
        <v-alert type="info" dense text class="mb-4">
          <strong>{{ workingOffers.length }}</strong> offer(s) selected for adjustment
          <span v-if="skippedMessage" class="ml-2">{{ skippedMessage }}</span>
        </v-alert>

        <!-- Adjustment Mode Selection -->
        <v-card outlined class="mb-4">
          <v-card-text>
            <v-radio-group v-model="adjustMode" row>
              <v-radio value="smart">
                <template v-slot:label>
                  <div>
                    <strong>Smart Offset</strong>
                    <div class="text-caption grey--text">
                      Maintain relative timing between offers
                    </div>
                  </div>
                </template>
              </v-radio>
              <v-radio value="manual">
                <template v-slot:label>
                  <div>
                    <strong>Manual</strong>
                    <div class="text-caption grey--text">
                      Adjust each offer individually
                    </div>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
            <v-alert type="info" dense text class="mt-2">
              <strong>Note:</strong> Smart Offset adjusts all offers together maintaining relative timing. Manual mode lets you set each offer's date individually while preserving its internal timing rules.
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- Smart Offset Mode -->
        <v-card v-if="adjustMode === 'smart'" outlined class="mb-4">
          <v-card-title class="text-subtitle-1">Smart Offset Settings</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <DateTimePicker
                    v-model="newBaseDate"
                    label="New Base Date"
                    outlined
                    dense
                    hide-details
                    prepend-inner-icon="mdi-calendar-edit"
                    :showUnix="false"
                />
                <div class="text-caption grey--text mt-1">
                  Set the new launch date - all offers will shift accordingly
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                    label="Reference Date (earliest offer)"
                    :value="formattedReferenceDate"
                    readonly
                    outlined
                    dense
                    hint="This is the earliest launch time among selected offers"
                    persistent-hint
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="grey">mdi-calendar-clock</v-icon>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>

            <v-alert v-if="offsetDays !== 0" :type="offsetDays > 0 ? 'success' : 'warning'" dense text class="mt-2">
              <strong>Offset: {{ offsetDaysFormatted }}</strong>
              <br>
              All {{ workingOffers.length }} offer(s) will shift by this amount
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- Manual Mode -->
        <v-card v-if="adjustMode === 'manual'" outlined class="mb-4">
          <v-card-title class="text-subtitle-1">Manual Adjustment</v-card-title>
          <v-card-text>
            <v-data-table
                :headers="manualHeaders"
                :items="manualAdjustmentData"
                :items-per-page="-1"
                hide-default-footer
                class="manual-adjustment-table"
            >
              <template v-slot:item.currentDate="{item}">
                <span>{{ item.currentDate }}</span>
              </template>
              <template v-slot:item.newDate="{item}">
                <div class="py-1">
                  <DateTimePicker
                      v-model="item.newLaunchTime"
                      outlined
                      :showUnix="false"
                      hide-details
                  />
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <!-- Preview Changes -->
        <v-card outlined class="mb-4">
          <v-card-title class="text-subtitle-1">
            <v-icon left small>mdi-eye-outline</v-icon>
            Preview Changes
          </v-card-title>
          <v-card-text>
            <v-simple-table dense>
              <thead>
              <tr>
                <th>Offer Name</th>
                <th>Current Launch (UTC)</th>
                <th></th>
                <th>New Launch (UTC)</th>
                <th>Change</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="item in previewData" :key="item.uid">
                <td class="text-caption">{{ item.name }}</td>
                <td class="text-caption">{{ item.oldDate }}</td>
                <td>
                  <v-icon small color="grey">mdi-arrow-right</v-icon>
                </td>
                <td class="text-caption font-weight-bold">{{ item.newDate }}</td>
                <td>
                  <v-chip x-small :color="item.diff === 'No change' ? 'grey' : (item.diff.startsWith('+') ? 'green' : 'orange')" text-color="white">
                    {{ item.diff }}
                  </v-chip>
                </td>
              </tr>
              </tbody>
            </v-simple-table>

            <v-alert v-if="previewData.length === 0" type="warning" dense text class="mt-2">
              No changes to preview. Please adjust the dates.
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- Advanced Settings -->
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <template v-slot:default="{ open }">
                <v-row no-gutters>
                  <v-col cols="4">
                    <v-icon left small>mdi-cog-outline</v-icon>
                    Advanced Settings
                  </v-col>
                  <v-col cols="8" class="text--secondary">
                    <v-fade-transition leave-absolute>
                      <span v-if="!open" class="text-caption">
                        Optional: Set resetTime, cooldown, quantity for all offers
                      </span>
                    </v-fade-transition>
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                      v-model.number="advancedSettings.resetTime"
                      label="Reset Time"
                      type="number"
                      outlined
                      dense
                      clearable
                      hint="e.g., 86400 (24 hours)"
                      persistent-hint
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                      v-model.number="advancedSettings.cooldown"
                      label="Cooldown"
                      type="number"
                      outlined
                      dense
                      clearable
                      hint="e.g., 3600 (1 hour)"
                      persistent-hint
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                      v-model.number="advancedSettings.quantity"
                      label="Quantity"
                      type="number"
                      outlined
                      dense
                      clearable
                      hint="e.g., 0 (unlimited)"
                      persistent-hint
                  />
                </v-col>
              </v-row>
              <v-alert type="info" dense text>
                These settings will be applied to all selected offers if values are provided.
              </v-alert>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-divider/>

      <v-card-actions>
        <v-spacer/>
        <v-btn text @click="close">
          Cancel
        </v-btn>
        <v-btn
            color="orange"
            @click="applyChanges"
            :disabled="!canApply"
            :loading="loading"
        >
          <v-icon left small>mdi-check</v-icon>
          Apply Changes ({{ workingOffers.length }} offers)
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {SmartOfferTimeUtils} from "@/modules/redactor/utils/SmartOfferTimeUtils";
import DateTimePicker from "@/components/DateTimePicker/DateTimePicker.vue";
import Store from "@/store";

const MILLISECONDS_PER_SECOND = 1000;

export default {
  name: "r-smart-offer-adjust-time-dialog",
  components: {DateTimePicker},
  data: () => ({
    MILLISECONDS_PER_SECOND,
    dialog: false,
    loading: false,

    // Working data
    workingOffers: [],

    // Skip info
    skippedInfo: {
      total: 0,
      readonly: 0,
      noTime: 0
    },

    // Adjustment mode: 'smart' or 'manual'
    adjustMode: "smart",

    // Smart offset settings
    newBaseDate: 0,

    // Manual adjustment data
    manualAdjustmentData: [],

    // Advanced settings
    advancedSettings: {
      resetTime: null,
      cooldown: null,
      quantity: null
    },

    // Table headers for manual mode
    manualHeaders: [
      {text: "Offer Name", value: "name", sortable: false},
      {text: "Current Launch", value: "currentDate", sortable: false},
      {text: "New Launch", value: "newDate", sortable: false, width: "300px"}
    ],

    // Callbacks
    resolveCallback: null,
    rejectCallback: null
  }),

  computed: {
    skippedMessage() {
      if (this.skippedInfo.total === 0) {
        return '';
      }

      const parts = [];
      if (this.skippedInfo.noTime > 0) {
        parts.push(`${this.skippedInfo.noTime} no launch time`);
      }
      if (this.skippedInfo.readonly > 0) {
        parts.push(`${this.skippedInfo.readonly} locked/global`);
      }

      return `(${this.skippedInfo.total} skipped: ${parts.join(', ')})`;
    },

    referenceDate() {
      return SmartOfferTimeUtils.getReferenceDate(this.workingOffers);
    },

    formattedReferenceDate() {
      return SmartOfferTimeUtils.formatDate(this.referenceDate);
    },

    offsetDays() {
      if (this.newBaseDate === 0 || this.referenceDate === 0) {
        return 0;
      }
      return SmartOfferTimeUtils.calculateOffsetDays(this.newBaseDate, this.referenceDate);
    },

    offsetDaysFormatted() {
      if (this.offsetDays === 0) {
        return "No change";
      } else if (this.offsetDays > 0) {
        return `+${this.offsetDays} days`;
      } else {
        return `${this.offsetDays} days`;
      }
    },

    adjustedOffers() {
      try {
        if (this.adjustMode === 'smart') {
          if (this.newBaseDate === 0) {
            return [];
          }
          return SmartOfferTimeUtils.applySmartOffsetToMany(
              this.workingOffers,
              this.newBaseDate,
              this.advancedSettings,
              { strict: false }
          );
        } else {
          // Manual mode
          const offerDateMap = this.manualAdjustmentData.map(item => ({
            offer: item.offer,
            newLaunchTime: item.newLaunchTime
          }));
          return SmartOfferTimeUtils.applyManualAdjustments(
              offerDateMap,
              this.advancedSettings
          );
        }
      } catch (error) {
        console.error("Error calculating adjusted offers:", error);
        return [];
      }
    },

    previewData() {
      if (this.adjustedOffers.length === 0) {
        return [];
      }
      return SmartOfferTimeUtils.generatePreviewData(
          this.workingOffers,
          this.adjustedOffers
      );
    },

    canApply() {
      if (this.loading) {
        return false;
      }
      if (this.workingOffers.length === 0) {
        return false;
      }
      if (this.adjustMode === 'smart' && this.newBaseDate === 0) {
        return false;
      }
      if (this.adjustedOffers.length === 0) {
        return false;
      }
      return true;
    }
  },

  methods: {
    /**
     * Show the dialog
     * @param {Array} offers - Array of SmartOffer objects to adjust
     * @param {number} totalSkipped - Total number of skipped offers (optional)
     * @param {number} readonlyCount - Number of readonly/locked offers skipped (optional)
     * @param {number} noTimeCount - Number of offers without launch times skipped (optional)
     * @returns {Promise}
     */
    show(offers, totalSkipped = 0, readonlyCount = 0, noTimeCount = 0) {
      return new Promise((resolve, reject) => {
        // Validate offers
        const validation = SmartOfferTimeUtils.validateOffers(offers);
        if (!validation.valid) {
          this.$alert(
              `Cannot adjust times:\n${validation.errors.join('\n')}`,
              "Validation Error",
              "error"
          );
          reject(new Error(validation.errors.join(', ')));
          return;
        }

        // Initialize
        this.workingOffers = offers;
        this.skippedInfo = {
          total: totalSkipped,
          readonly: readonlyCount,
          noTime: noTimeCount
        };
        this.adjustMode = "smart";

        // Set default new base date to current date/time
        this.newBaseDate = Math.floor(Date.now() / this.MILLISECONDS_PER_SECOND);

        // Initialize manual adjustment data
        this.initManualAdjustmentData();

        // Reset advanced settings
        this.advancedSettings = {
          resetTime: null,
          cooldown: null,
          quantity: null
        };

        // Setup callbacks
        this.resolveCallback = resolve;
        this.rejectCallback = reject;

        // Show dialog
        this.dialog = true;
      });
    },

    initManualAdjustmentData() {
      this.manualAdjustmentData = this.workingOffers.map(offer => {
        const currentTime = SmartOfferTimeUtils.getFirstLaunchTime(offer);
        return {
          offer: offer,
          name: offer.name || offer.key || offer.uid,
          currentDate: SmartOfferTimeUtils.formatDate(currentTime),
          newLaunchTime: currentTime // Default to current time
        };
      });
    },

    async applyChanges() {
      try {
        this.loading = true;

        // Get adjusted offers
        const adjustedOffers = this.adjustedOffers;

        if (adjustedOffers.length === 0) {
          throw new Error("No adjusted offers to save");
        }

        // Save to Firebase using bulk update
        await Store.dispatch('redactor-store-smart-offer/pushValues', adjustedOffers);

        // Success
        if (this.resolveCallback) {
          this.resolveCallback({
            offers: adjustedOffers,
            count: adjustedOffers.length,
            mode: this.adjustMode
          });
          this.resolveCallback = null;
          this.rejectCallback = null;
        }

        this.close();
      } catch (error) {
        console.error("Error applying changes:", error);
        this.$alert(
            `Failed to apply changes: ${error.message}`,
            "Error",
            "error"
        );
      } finally {
        this.loading = false;
      }
    },

    close() {
      this.dialog = false;
      this.loading = false;
      this.workingOffers = [];
      this.manualAdjustmentData = [];
      this.skippedInfo = {
        total: 0,
        readonly: 0,
        noTime: 0
      };

      // Only reject if callbacks still exist (i.e., not already resolved)
      if (this.rejectCallback) {
        this.rejectCallback();
      }
      // Always clear both callbacks to prevent multiple invocations
      this.resolveCallback = null;
      this.rejectCallback = null;
    }
  }
};
</script>

<style scoped>
.text-caption {
  font-size: 0.75rem !important;
}

.manual-adjustment-table ::v-deep tbody tr {
  height: 60px !important;
}

.manual-adjustment-table ::v-deep tbody td {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}
</style>
